/**
 * Keeps this device's checklist and the server's copy converging.
 *
 * The whole protocol is: export the local backup, encrypt it, PUT it; GET it,
 * decrypt it, `importBackup(..., 'merge')`. Both halves already existed for
 * the backup file feature, and because every entry carries its own
 * `updatedAt`, the merge resolves per item — two devices ticking different
 * rewards never conflict.
 *
 * Every operation goes through one promise chain, so there is never a second
 * request in flight arguing with the first, and a push always exports AFTER
 * any merge that preceded it.
 */

import { isBackupV1, type BackupV1 } from '@/domain/progress.types';
import type { StorageAdapter } from '@/storage/StorageAdapter';
import { useSync, type SyncFault } from '@/state/syncStore';
import { DecryptError, docIdFor, open, seal } from './crypto';
import { isEnvelope, syncUrl, type ConflictBody, type Envelope } from './protocol';

/** Long enough to coalesce a burst of ticking, short enough to feel live. */
const DEBOUNCE_MS = 5_000;
/** ...but never sit on changes for longer than this while someone keeps ticking. */
const MAX_WAIT_MS = 60_000;
/** First retry after a failed upload; doubles up to the cap. */
const RETRY_MS = 15_000;
const RETRY_CAP_MS = 5 * 60_000;

export class SyncFailure extends Error {
  constructor(readonly fault: SyncFault) {
    super(fault);
    this.name = 'SyncFailure';
  }
}

export interface SyncEngineDeps {
  local: StorageAdapter;
  fetch?: typeof globalThis.fetch;
  now?: () => number;
}

export class SyncEngine {
  private readonly local: StorageAdapter;
  private readonly fetchFn: typeof globalThis.fetch;
  private readonly now: () => number;

  /** This device's belief about the server's revision. 0 means "not yet read". */
  private rev = 0;
  /**
   * Set the moment anything local changes, cleared only by a successful
   * upload. Without it a push that failed while offline would be forgotten,
   * and those ticks would sit on this device until the next unrelated edit.
   */
  private dirty = false;
  private retryMs = 0;
  /**
   * `dirty` lives in memory, so a reload forgets that this device still owed
   * an upload. The first reconcile of a session therefore always pushes —
   * one extra write per page load, against silently stranding ticks that were
   * made offline and never sent.
   */
  private reconciled = false;
  /** Serialises every request; see the class comment. */
  private chain: Promise<unknown> = Promise.resolve();
  private timer: ReturnType<typeof setTimeout> | null = null;
  private firstQueuedAt: number | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(deps: SyncEngineDeps) {
    this.local = deps.local;
    this.fetchFn = deps.fetch ?? globalThis.fetch.bind(globalThis);
    this.now = deps.now ?? Date.now;
  }

  private get code(): string | null {
    return useSync.getState().code;
  }

  /**
   * Fires after a pull changed local data, so the adapter can push the merged
   * snapshot into the UI — a same-tab write raises no `storage` event.
   */
  onRemoteChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Queue a push. Repeated calls coalesce, but cannot postpone one forever. */
  schedulePush(): void {
    if (!this.code) return;
    this.dirty = true;
    const now = this.now();
    this.firstQueuedAt ??= now;
    const waited = now - this.firstQueuedAt;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.push(), Math.max(0, Math.min(DEBOUNCE_MS, MAX_WAIT_MS - waited)));
  }

  /** Send anything queued right now — used when the tab is being hidden. */
  flush(): Promise<void> {
    if (this.timer === null) return Promise.resolve();
    return this.push();
  }

  /** Resolves once nothing is in flight. Never rejects — see `run`. */
  idle(): Promise<void> {
    return this.chain.then(() => undefined);
  }

  pull(): Promise<void> {
    return this.run(async () => {
      const merged = await this.doPull();
      if (merged) this.announce();
    });
  }

  push(): Promise<void> {
    return this.run(async () => {
      try {
        await this.doPush();
      } catch (err) {
        this.scheduleRetry();
        throw err;
      }
    });
  }

  /**
   * Catch up in both directions: take what the others did, then send what this
   * device still owes. This is what a tab regaining focus, or a network coming
   * back, should do — a bare pull would silently strand unsent ticks.
   */
  resume(): Promise<void> {
    return this.run(async () => {
      const merged = await this.doPull();
      if (merged) this.announce();
      if (this.dirty || !this.reconciled) await this.doPush();
      this.reconciled = true;
    });
  }

  private scheduleRetry(): void {
    this.retryMs = this.retryMs === 0 ? RETRY_MS : Math.min(this.retryMs * 2, RETRY_CAP_MS);
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.push().catch(() => undefined), this.retryMs);
  }

  /**
   * Adopt `code` on this device. A device joining an existing checklist takes
   * the cloud copy wholesale when it has nothing of its own to lose, and
   * merges otherwise — two devices that each seeded their own "Main" profile
   * would otherwise both keep it, and the user would see the profile twice.
   */
  pair(code: string): Promise<void> {
    // Adopted before entering the chain: `run` is a no-op without a code.
    useSync.getState().setCode(code);
    this.rev = 0;
    this.reconciled = false;
    return this.run(async () => {
      const remote = await this.get(code);
      if (remote) {
        const payload = await this.decode(code, remote);
        await this.local.importBackup(payload, (await this.isPristine()) ? 'replace' : 'merge');
        this.rev = remote.rev;
        this.announce();
      }
      await this.doPush();
    });
  }

  /** Stop syncing here. `erase` also removes the server's copy for everyone. */
  stop(erase: boolean): Promise<void> {
    return this.run(async () => {
      const code = this.code;
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
      this.firstQueuedAt = null;
      if (erase && code) {
        await this.fetchFn(syncUrl(await docIdFor(code)), { method: 'DELETE' }).catch(() => null);
      }
      this.rev = 0;
      useSync.getState().setCode(null);
    });
  }

  /* ----------------------------- internals ----------------------------- */

  private announce(): void {
    for (const listener of this.listeners) listener();
  }

  /** Nothing worth keeping on this device: a fresh install, or one seeded profile. */
  private async isPristine(): Promise<boolean> {
    const profiles = await this.local.listProfiles();
    if (profiles.length > 1) return false;
    for (const profile of profiles) {
      const snapshot = await this.local.getProgress(profile.id);
      for (const set of Object.values(snapshot.sets)) {
        for (const entry of Object.values(set)) if (entry.done) return false;
      }
    }
    return true;
  }

  private run<T>(op: () => Promise<T>): Promise<T> {
    const next = this.chain.then(async () => {
      if (!this.code) return undefined as T;
      useSync.getState().report('syncing');
      try {
        const result = await op();
        useSync.getState().markSynced(this.now());
        return result;
      } catch (err) {
        const fault = err instanceof SyncFailure ? err.fault : 'network';
        useSync.getState().report('error', fault);
        throw err;
      }
    });
    // The chain must survive a rejection, or one failure freezes sync forever.
    this.chain = next.catch(() => undefined);
    return next;
  }

  private async get(code: string): Promise<Envelope | null> {
    let response: Response;
    try {
      response = await this.fetchFn(syncUrl(await docIdFor(code)), { cache: 'no-store' });
    } catch {
      throw new SyncFailure('network');
    }
    if (response.status === 404) return null;
    if (!response.ok) throw new SyncFailure('server');
    const body: unknown = await response.json().catch(() => null);
    if (!isEnvelope(body)) throw new SyncFailure('server');
    return body;
  }

  /** Decrypt and sanity-check a payload before it is allowed near local data. */
  private async decode(code: string, envelope: Envelope): Promise<BackupV1> {
    let payload: unknown;
    try {
      payload = await open(code, envelope);
    } catch (err) {
      throw new SyncFailure(err instanceof DecryptError ? 'code' : 'server');
    }
    // A backup written by a newer version of the app would be read as an empty
    // snapshot by `getProgress` and then merged over good data. Refuse instead.
    if (!isBackupV1(payload)) throw new SyncFailure('schema');
    for (const snapshot of Object.values(payload.progress)) {
      if (snapshot.schemaVersion !== 1) throw new SyncFailure('schema');
    }
    return payload;
  }

  /** Returns true when local data actually changed. */
  private async doPull(): Promise<boolean> {
    const code = this.code;
    if (!code) return false;
    const remote = await this.get(code);
    if (!remote) return false;
    if (remote.rev === this.rev) return false;
    await this.local.importBackup(await this.decode(code, remote), 'merge');
    this.rev = remote.rev;
    return true;
  }

  private async doPush(): Promise<void> {
    const code = this.code;
    if (!code) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.firstQueuedAt = null;

    // First push of the session writes blind; a 409 then tells us where we are.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      const backup = await this.local.exportBackup();
      const envelope = await seal(code, backup, this.rev + 1);
      let response: Response;
      try {
        response = await this.fetchFn(syncUrl(await docIdFor(code)), {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(envelope),
        });
      } catch {
        throw new SyncFailure('network');
      }

      if (response.ok) {
        this.rev = envelope.rev;
        this.reconciled = true;
        this.dirty = false;
        this.retryMs = 0;
        return;
      }

      if (response.status === 409 && attempt === 0) {
        // Someone else wrote first. Merge their copy in, then send the union.
        const body = (await response.json().catch(() => null)) as ConflictBody | null;
        const current = body?.current ?? null;
        if (current && isEnvelope(current)) {
          await this.local.importBackup(await this.decode(code, current), 'merge');
          this.rev = current.rev;
          this.announce();
        } else {
          this.rev = 0;
        }
        continue;
      }

      throw new SyncFailure(response.status === 409 ? 'network' : 'server');
    }
  }
}
